import {
    Button,
    Card,
    CardActions,
    CardContent,
    Typography,
} from '@mui/material';
import * as yup from 'yup';
import {
    FormInputGroup,
    FormSelect,
    FormText,
    FormSubmitButton,
    FormProvider,
} from 'components/form';
import { CarGasConnectionDetails, SupplierInfo } from 'types';
import { useEffect, useMemo, useState } from 'react';
import {
    getCargasBackoffice,
    saveBackoffice,
    useLazyCargasTestQuery,
} from 'store/services/third-party-backoffice';
import { activateSnackbar } from 'store/features/ui';
import { useDispatch } from 'react-redux';
import { FieldValues } from 'react-hook-form';
import { StoredUser } from 'store/features/authentication';
import {
    BackofficeTestDialog,
    BackofficeTestDialogProps,
} from '../BackofficeTestDialog/BackofficeTestDialog';

export interface CarGasBackofficeFormProps {
    supplierId: string;
    supplierInfo: SupplierInfo;
    user: StoredUser;
}

const backofficeSchema = yup.object<{
    [k in keyof CarGasConnectionDetails]: yup.AnySchema;
}>({
    url: yup.string().required(),
    apikey: yup.string().required(),
    username: yup.string().required(),
    group_field: yup.string().required(),
    enabled: yup.boolean().required(),
});

/** Third-party backoffice configuration form for the CarGas backoffice platform. */
export const CarGasBackofficeForm = ({
    supplierId,
    supplierInfo,
    user,
}: CarGasBackofficeFormProps) => {
    const [testCargasQuery] = useLazyCargasTestQuery();
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testResponse, setTestResponse] =
        useState<BackofficeTestDialogProps['status']>(null);

    const [cargasBackofficeInfo, setCargasBackofficeInfo] =
        useState<CarGasConnectionDetails>();

    const defaultValues = useMemo(() => {
        return {
            url: cargasBackofficeInfo?.url ?? '',
            apikey: cargasBackofficeInfo?.apikey ?? '',
            username: cargasBackofficeInfo?.username ?? '',
            group_field: cargasBackofficeInfo?.group_field ?? 'ServiceZone',
            enabled: cargasBackofficeInfo?.enabled ?? true,
        };
    }, [cargasBackofficeInfo]);

    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();

    // Load existing Cargas backoffice data.
    useEffect(() => {
        getCargasBackoffice(supplierId).then(
            (result: CarGasConnectionDetails) => {
                setCargasBackofficeInfo(result);
                setIsLoading(false);
            }
        );
    }, [supplierId]);

    const onSave = (cargasDetails: CarGasConnectionDetails) => {
        saveBackoffice(supplierId, supplierInfo, user, 'cargas', cargasDetails)
            .then(() => {
                dispatch(
                    activateSnackbar({
                        message: 'Saved Successfully',
                        severity: 'success',
                    })
                );
            })
            .catch((error) => {
                dispatch(
                    activateSnackbar({
                        message: JSON.stringify(error),
                        severity: 'error',
                    })
                );
            });
    };

    const onSaveError = (error: FieldValues) => {
        const messages = Object.keys(error).map((key) => error[key]?.message);
        dispatch(
            activateSnackbar({
                message: JSON.stringify(messages),
                severity: 'error',
            })
        );
    };

    const testBackoffice = () => {
        testCargasQuery({ supplierId })
            .then((response) => {
                if (response.isSuccess) setTestResponse('success');
                else setTestResponse('fail');
            })
            .catch(() => {
                setTestResponse('fail');
            })
            .finally(() => {
                setTestDialogOpen(true);
            });
    };

    return (
        <div data-testid="CarGasBackofficeForm">
            {!isLoading ? (
                <Card>
                    <CardContent>
                        <FormProvider
                            defaultValues={defaultValues}
                            validationSchema={backofficeSchema}
                        >
                            <Typography variant="h6">
                                CarGas Connection Details
                            </Typography>
                            <FormInputGroup>
                                <FormText field="url" label="Remote URL" />
                            </FormInputGroup>
                            <FormInputGroup>
                                <FormText field="apikey" label="API Key" />
                            </FormInputGroup>
                            <FormInputGroup>
                                <FormText field="username" label="Username" />
                            </FormInputGroup>
                            <FormInputGroup>
                                <FormSelect
                                    field="group_field"
                                    options={[
                                        {
                                            name: 'Delivery Zone',
                                            value: 'DeliveryZone',
                                        },
                                        {
                                            name: 'Service Zone',
                                            value: 'ServiceZone',
                                        },
                                    ]}
                                />
                            </FormInputGroup>
                            <CardActions
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Button onClick={testBackoffice}>Test</Button>
                                <FormSubmitButton
                                    onValidHandler={(
                                        input: CarGasConnectionDetails
                                    ) => onSave(input)}
                                    onErrorHandler={onSaveError}
                                    buttonLabel="Save"
                                />
                            </CardActions>
                        </FormProvider>
                    </CardContent>
                </Card>
            ) : null}

            <BackofficeTestDialog
                setOpen={setTestDialogOpen}
                open={testDialogOpen}
                status={testResponse}
            />
        </div>
    );
};
